import { useEffect, useState } from "react";
import axios from "axios";
import { ArrowSquareOut } from "@phosphor-icons/react";
import { OverallBanner, ComponentRow, IncidentDay } from "../components/system/PublicStatusParts";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function dayBuckets(incidents, days) {
  const out = [];
  for (let i = 0; i < days; i++) {
    const day = new Date(); day.setHours(0, 0, 0, 0); day.setDate(day.getDate() - i);
    const next = new Date(day); next.setDate(day.getDate() + 1);
    out.push({ day, items: incidents.filter((inc) => { const s = new Date(inc.started_at); return s >= day && s < next; }) });
  }
  return out;
}

/** Public, read-only status page (fixlr.app/status, also served on status.fixlr.app). No login, no internals. */
export default function PublicStatusPage() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    document.title = "Fixlr Status";
    let alive = true;
    const load = () => axios.get(`${API}/public/status`).then(({ data }) => { if (alive) { setData(data); setError(null); } }).catch(() => alive && setError("Status information is temporarily unavailable."));
    load();
    const t = setInterval(load, 60000);
    return () => { alive = false; clearInterval(t); };
  }, []);

  return (
    <div className="min-h-screen bg-[#f6f7f9] text-slate-900" data-testid="public-status-page">
      <header className="border-b border-slate-200 bg-white/80 backdrop-blur">
        <div className="max-w-3xl mx-auto px-5 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/fixlr-logo-dark.png" alt="Fixlr" className="h-8 w-auto" />
            <span className="text-sm font-semibold tracking-wide text-slate-500 border-l border-slate-200 pl-3 font-[Chivo]">Status</span>
          </div>
          <a href="https://fixlr.app" className="text-xs text-slate-500 hover:text-slate-900 inline-flex items-center gap-1" data-testid="public-status-home">fixlr.app <ArrowSquareOut size={12} /></a>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-5 py-8 space-y-8">
        {error && !data && <div className="rounded-xl border border-slate-200 bg-white p-6 text-sm text-slate-500" data-testid="public-status-error">{error}</div>}
        {!error && !data && <div className="rounded-xl border border-slate-200 bg-white p-6 text-sm text-slate-400">Loading status…</div>}
        {data && (
          <>
            <OverallBanner data={data} />
            <section className="rounded-2xl border border-slate-200 bg-white px-6" data-testid="public-components">
              {data.components.map((c) => <ComponentRow key={c.key} c={{ ...c, days: data.days }} />)}
              <div className="flex items-center gap-4 py-3 text-[11px] text-slate-500 border-t border-slate-200">
                <span className="flex items-center gap-1"><span className="h-2 w-3 rounded-sm bg-emerald-500" /> operational</span>
                <span className="flex items-center gap-1"><span className="h-2 w-3 rounded-sm bg-amber-400" /> partial disruption</span>
                <span className="flex items-center gap-1"><span className="h-2 w-3 rounded-sm bg-red-500" /> outage</span>
                <span className="flex items-center gap-1"><span className="h-2 w-3 rounded-sm bg-slate-200" /> no data</span>
                <span className="ml-auto">each block = 1 hour</span>
              </div>
            </section>
            <section data-testid="public-incidents">
              <h2 className="text-base md:text-lg font-bold font-[Chivo] mb-2">Past incidents</h2>
              <div className="rounded-2xl border border-slate-200 bg-white px-6">
                {dayBuckets(data.incidents, data.days).map((b) => <IncidentDay key={b.day.toISOString()} day={b.day} items={b.items} />)}
              </div>
            </section>
          </>
        )}
      </main>
      <footer className="max-w-3xl mx-auto px-5 pb-10 text-[11px] text-slate-400">Fixlr · This page is read-only and updates automatically. Times are shown in your local time zone.</footer>
    </div>
  );
}
